/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import StoreProxy from 'storeProxy';
import { Product } from 'types/IProducts';
import { TickerData } from 'types/Ticker';

import { initializeStateUpdates, processTickerUpdate } from './ticker.store.helpers';
import type { TickerStore } from './ticker.types';

// For numerics, we use Map<number, number> instead of Record<number, number>/
// For String. use  Record<string, string>;

const useTickerStoreBase = create<TickerStore>()(
  subscribeWithSelector(
    devtools(
      set => ({
        // #region States
        state: {
          tickerObj: {},
          optionsTickerObj: {},
          spotPrice: {},
          markPrice: new Map(),
          greeks: {
            delta: new Map(),
            gamma: new Map(),
            theta: new Map(),
            vega: new Map(),
          },
          askIV: new Map(),
          bidIV: new Map(),
          bidQty: new Map(),
          askQty: new Map(),
          bestAsk: new Map(),
          bestBuy: new Map(),
          impliedVolatility: new Map(),
          priceBands: new Map(),
          oi: new Map(),
          callOi: new Map(),
          putOi: new Map(),
          markIV: new Map(),
          oiChange: new Map(),
          callTurnoverUsd: new Map(),
          putTurnoverUsd: new Map(),
          tickerMapById: new Map(),
        },

        // #endregion

        // #region Actions
        actions: {
          updateTickerData: (tickerData: TickerData) => {
            try {
              const reduxStore = StoreProxy.getState();
              const allProducts = reduxStore.trade.products as Product[];
              const product =
                tickerData && allProducts
                  ? allProducts[tickerData?.product_id as number]
                  : {};
              const { spot_index_id } = product || {};
              const spotSymbol =
                reduxStore?.trade?.spot_indices?.[spot_index_id as number]?.symbol ??
                null;

              set(
                store => {
                  const stateUpdates = initializeStateUpdates(store.state);
                  processTickerUpdate(tickerData, stateUpdates, product, spotSymbol);

                  return { state: stateUpdates };
                },
                false,
                'updateTickerData' // -> This is used for devtools
              );
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('updateTickerData failed', e);
            }
          },

          // Writing the two function separately instead of calling updateTickerData inside it as to avoid calling StoreProxy.getState() multiple times
          updateAllTickers: (tickerList: TickerData[]) => {
            try {
              // Fetch store state once for all tickers (performance optimization)
              const reduxStore = StoreProxy.getState();
              const allProducts = reduxStore.trade.products as Product[];
              const spotIndices = reduxStore?.trade?.spot_indices;

              // Batch all updates into a single set() call
              set(
                store => {
                  const stateUpdates = initializeStateUpdates(store.state);

                  // Process all tickers using the utility function
                  tickerList.forEach(tickerData => {
                    const product = allProducts?.[tickerData?.product_id as number] || {};
                    const { spot_index_id } = product;
                    const spotSymbol =
                      spotIndices?.[spot_index_id as number]?.symbol ?? null;

                    processTickerUpdate(tickerData, stateUpdates, product, spotSymbol);
                  });

                  return { state: stateUpdates };
                },
                false,
                'updateAllTickers' // -> This is used for devtools
              );
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('updateAllTickers failed', e);
            }
          },

          updateOpenPositionSpotPrice: (data: { s: string; p: number }) => {
            set(
              store => ({
                state: {
                  ...store.state,
                  spotPrice: {
                    ...store.state.spotPrice,
                    [data.s]: Number(data.p),
                  },
                },
              }),
              false,
              'updateOpenPositionSpotPrice' // -> This is used for devtools
            );
          },
        },

        // #endregion
      }),
      {
        name: 'TickerStore',
        serialize: {
          options: true,
        },
      }
    )
  )
);

export { useTickerStoreBase };
